import {Exclude, Expose, instanceToPlain, plainToClass, Transform, Type} from "class-transformer";
import {SystemAlertBaseValidator} from "./alert";
import * as t from "io-ts";
import {GeoCoord} from "./geo-coord";
import {Entity} from "./entity";
import {GeofenceEventEntity, GeofenceEventValidator} from "./geofence-event";
import {getDatabaseCodeTransformer, getTransportCategoryTransformer} from "./transfomers/transformers";
import {TransportCategorizable, transportCategory, TransportCategory} from "./transport-category";
import {toTransportRejectionType, TransportRejectionType} from "./transport-rejection-type";
import {DatabaseCode, getDatabaseCode, toDatabaseCode} from "./database-code";
import {validateJSON, validateJSONNow} from "../web/validation";
import {TransportGauge, TransportGaugeArrayValidator, TransportGaugeEntity} from "./transport-gauge";
import {Message, MessageArrayValidator, MessageEntity} from "./message";
import {Supplemental, SupplementalArrayValidator} from "./supplemental";
import {RestrictionArrayValidator, RestrictionEntity} from "./restriction";
import {toValveType, ValveType} from "./valve-type";
import {toCargoSubtype} from "./cargo-subtype";
import {
  RrcEp10WasteManifestBuildStatusCodec,
  RrcEp10WasteManifestBuildStatusEntity
} from "./rrc/rrc-ep10-waste-manifest-build-status";
import {ScadaCalcState, toScadaCalcState} from "./scada-calc-state";

/** Validator for transport types. */
export const TransportValidator = t.intersection([
  t.type({
    id: t.string,
    personId: t.string,
    containerId: t.string,
    waterTypeId: t.string,
    type: t.string,
    trailerId: t.string,
    quantityDecimal: t.number,
    manualEntry: t.boolean,
  }),
  t.partial({
    gauges: TransportGaugeArrayValidator,
    resourceId: t.string,
    ticketNumber: t.string,
    workOrderNumber: t.string,
    internalWorkOrderNumber: t.string,
    regulatoryNumber: t.string,
    leaseId: t.string,
    leaseName: t.string,
    shiftId: t.string,
    containerName: t.string,
    /** The container's short name, e.g. "T-4" for "Tank 4"; filled with the transport's extras. */
    containerAbbr: t.string,
    trailerName: t.string,
    trailerCompanyId: t.string,
    trailerCompanyName: t.string,
    parentCompanyId: t.string,
    parentCompanyName: t.string,
    waterTypeName: t.string,
    waterTypeAbbr: t.string,
    serviceTypeName: t.string,
    serviceTypeAbbr: t.string,
    quantityUnitOfMeasure: t.string,
    serviceTypeId: t.string,
    siteId: t.string,
    siteName: t.string,
    siteCompanyId: t.string,
    siteCompanyName: t.string,
    siteCompanyType: t.string,
    /** The geofence site group of the transport's site, for matching it to a geofence visit at a sister site. */
    siteGeofenceSiteGroupId: t.string,
    personName: t.string,
    personFirstName: t.string,
    personLastName: t.string,
    vehicleId: t.string,
    vehicleName: t.string,
    wellId: t.string,
    wellName: t.string,
    wellStageId: t.string,
    stage: t.string,
    routeIds: t.array(t.string),
    routeNames: t.array(t.string),
    routeGroupIds: t.array(t.string),
    routeGroupNames: t.array(t.string),
    rigId: t.string,
    rigName: t.string,
    latCoord: t.number,
    longCoord: t.number,
    regionId: t.string,
    regionGeographicName: t.string,
    transitTime: t.number,
    inDttm: t.number,
    outDttm: t.number,
    skipGeofenceEvent: t.boolean,
    onSiteDuration: t.number,
    driverQty: t.number,
    scadaQty: t.number,
    scadaTopQty: t.number,
    scadaBottomQty: t.number,
    scada2Qty: t.number,
    scada2TopQty: t.number,
    scada2BottomQty: t.number,
    scadaQtyCalcState: t.string,
    scadaQtyCalcErrorDetail: t.string,
    manualCalcQty: t.number,
    manualCalcTopQty: t.number,
    manualCalcBottomQty: t.number,
    scadaTopGauge: t.number,
    scadaBottomGauge: t.number,
    scadaGaugeCargoSubtype: t.string,
    scadaTopGauge2: t.number,
    scadaBottomGauge2: t.number,
    scadaGaugeCargoSubtype2: t.string,
    scada2TopGauge: t.number,
    scada2BottomGauge: t.number,
    scada2GaugeCargoSubtype: t.string,
    scada2TopGauge2: t.number,
    scada2BottomGauge2: t.number,
    scada2GaugeCargoSubtype2: t.string,
    scadaGaugeCalcState: t.string,
    scadaGaugeCalcErrorDetail: t.string,
    fillRate: t.number,
    gaugeQty: t.number,
    disposalQty: t.number,
    /** Start Oil Specific Field */
    standardQuantity: t.number,
    netQuantity: t.number,
    observedTemperature: t.number,
    topTemperature: t.number,
    bottomTemperature: t.number,
    temperatureUom: t.string,
    observedPressure: t.number,
    pressureUnitOfMeasure: t.string,
    observedGravity: t.number,
    bswPercentage: t.number,
    gravityAt60: t.number,
    rejectionType: t.string,
    rejectionComment: t.string,
    /** End Oil Specific Fields */
    sealOff: t.string,
    sealOn: t.string,
    sealValveType: t.string,
    seal2Off: t.string,
    seal2On: t.string,
    seal2ValveType: t.string,
    seal3Off: t.string,
    seal3On: t.string,
    seal3ValveType: t.string,
    sealOffDttm: t.number,
    sealOnDttm: t.number,
    seal2OffDttm: t.number,
    seal2OnDttm: t.number,
    seal3OffDttm: t.number,
    seal3OnDttm: t.number,
    meterReading: t.number,
    meterReadingEnd: t.number,
    meterReadingUom: t.string,
    odometer: t.number,
    odometerUom: t.string,
    nextStopSiteId: t.string,
    nextStopNote: t.string,
    nextStopDeclaredDttm: t.number,
    disableDemurrage: t.boolean,
    geofenceEvent: GeofenceEventValidator,
    messages: MessageArrayValidator,
    supplementals: SupplementalArrayValidator,
    restrictions: RestrictionArrayValidator,
    wellStageIsDefault: t.boolean,
    projectedWellStageIsDefault: t.boolean,
    subStage: t.string,
    companyId: t.string,
    companyType: t.string,
    siteParentSiteName: t.string,
    containerClusterName: t.string,
    containerScadaMode: t.string,
    containerLactMeterAttached: t.boolean,
    /** The second SCADA reading as driver activity sends it. */
    scadaQty2: t.number,
    triggeredAlerts: t.array(SystemAlertBaseValidator),
    holdingRecord: t.boolean,
    tabletHolding: t.boolean,
    rrcEp10WasteManifestBuildStatus: RrcEp10WasteManifestBuildStatusCodec,
    dbName: t.string,
    createDttm: t.number,
    lastModifiedDttm: t.number,
    category: t.string,
  }),
]);

/** Validator for an array of transport types. */
export const TransportArrayValidator = t.array(TransportValidator);

export type Transport = t.TypeOf<typeof TransportValidator>;
export type TransportArray = t.TypeOf<typeof TransportArrayValidator>;

/**
 * The type of transport.
 */
export type TransportType = DatabaseCode

const pickup: TransportType = {
  code: 'PICKUP',
  displayValue: {
    id: 'ots.ui.entity.transport.type.pickup',
    defaultMessage: 'Pickup',
  },
};

const delivery: TransportType = {
  code: 'DELIVERY',
  displayValue: {
    id: 'ots.ui.entity.transport.type.delivery',
    defaultMessage: 'Delivery',
  },
};

/**
 * Types for {@link Transport}.
 */
export const transportType = {
  pickup,
  delivery,
};

export const transportTypes = Object.values(transportType);

export const toTransportType = (code: string): TransportType => toDatabaseCode(code, transportTypes);
export const getTransportType = (code: string | undefined): TransportType | undefined => getDatabaseCode(code, transportTypes);

/**
 * A transport.
 */
@Exclude()
export class TransportEntity implements Entity, TransportCategorizable {
  @Expose() id!: string;
  @Expose() personId!: string;
  @Expose() personName?: string;
  @Expose() personFirstName?: string;
  @Expose() personLastName?: string;
  @Expose() shiftId?: string;
  @Expose() containerId!: string;
  @Expose() containerName?: string;
  @Expose() containerAbbr?: string;
  @Expose() siteId?: string;
  @Expose() siteName?: string;
  @Expose() siteCompanyId?: string;
  @Expose() siteCompanyName?: string;
  @Expose() siteCompanyType?: string;
  @Expose() waterTypeId!: string;
  @Expose() waterTypeName?: string;
  @Expose() waterTypeAbbr?: string;
  @Expose() serviceTypeId?: string;
  @Expose() serviceTypeName?: string;
  @Expose() serviceTypeAbbr?: string;

  @Expose()
  @Transform(getDatabaseCodeTransformer(toTransportType))
  type!: TransportType;

  @Expose() wellId?: string;
  @Expose() wellName?: string;
  @Expose() wellStageId?: string;
  @Expose({name: 'stage'}) wellStageName?: string;
  @Expose() routeIds?: string[]
  @Expose() routeNames?: string[]
  @Expose() routeGroupIds?: string[]
  @Expose() routeGroupNames?: string[]
  @Expose() rigId?: string;
  @Expose() rigName?: string;
  @Expose() trailerId!: string;
  @Expose() trailerName?: string;
  @Expose() trailerCompanyId?: string;
  @Expose() trailerCompanyName?: string;
  @Expose({name: 'parentCompanyId'}) trailerParentCompanyId?: string;
  @Expose({name: 'parentCompanyName'}) trailerParentCompanyName?: string;
  @Expose() vehicleId?: string;
  @Expose() vehicleName?: string;
  @Expose() resourceId?: string
  @Expose() ticketNumber?: string;
  @Expose() workOrderNumber?: string;
  @Expose() internalWorkOrderNumber?: string;
  @Expose() regulatoryNumber?: string
  @Expose() leaseId?: string
  @Expose() leaseName?: string
  @Expose() quantityDecimal!: number;
  @Expose() quantityUnitOfMeasure?: string;
  @Expose() driverQty?: number;
  @Expose() scadaQty?: number;
  @Expose() scadaTopQty?: number;
  @Expose() scadaBottomQty?: number;
  @Expose() scada2Qty?: number;
  @Expose() scada2TopQty?: number;
  @Expose() scada2BottomQty?: number;
  @Expose() @Transform(getDatabaseCodeTransformer(toScadaCalcState)) scadaQtyCalcState?: ScadaCalcState
  @Expose() scadaQtyCalcErrorDetail?: string
  @Expose() manualCalcQty?: number;
  @Expose() manualCalcTopQty?: number;
  @Expose() manualCalcBottomQty?: number;
  @Expose() scadaTopGauge?: number;
  @Expose() scadaBottomGauge?: number;
  @Expose() @Transform(getDatabaseCodeTransformer(toCargoSubtype)) scadaGaugeCargoSubtype?: string;
  @Expose() scadaTopGauge2?: number;
  @Expose() scadaBottomGauge2?: number;
  @Expose() @Transform(getDatabaseCodeTransformer(toCargoSubtype)) scadaGaugeCargoSubtype2?: string;
  @Expose() scada2TopGauge?: number;
  @Expose() scada2BottomGauge?: number;
  @Expose() @Transform(getDatabaseCodeTransformer(toCargoSubtype)) scada2GaugeCargoSubtype?: string;
  @Expose() scada2TopGauge2?: number;
  @Expose() scada2BottomGauge2?: number;
  @Expose() @Transform(getDatabaseCodeTransformer(toCargoSubtype)) scada2GaugeCargoSubtype2?: string;
  @Expose() @Transform(getDatabaseCodeTransformer(toScadaCalcState)) scadaGaugeCalcState?: ScadaCalcState
  @Expose() scadaGaugeCalcErrorDetail?: string
  @Expose() fillRate?: number;
  @Expose() gaugeQty?: number;
  @Expose() disposalQty?: number;
  @Expose() private latCoord?: number;
  @Expose() private longCoord?: number;
  @Expose() manualEntry!: boolean;
  @Expose() regionId?: string
  @Expose() regionGeographicName?: string;
  @Expose() transitTime?: number;
  @Expose() onSiteDuration?: number;
  @Expose() inDttm?: number;
  @Expose() outDttm?: number;
  @Expose() skipGeofenceEvent?: boolean;
  @Expose() disableDemurrage?: boolean;
  @Expose() readonly dbName?: string;
  @Expose() createDttm?: number;
  @Expose() lastModifiedDttm?: number;
  @Expose()
  @Transform(getTransportCategoryTransformer())
  category: TransportCategory = transportCategory.water;

  // Gauge Fields
  @Expose()
  @Type(() => TransportGaugeEntity)
  gauges?: TransportGaugeEntity[]

  // Meter Fields
  @Expose({name: 'meterReading'}) meterReadingStart?: number;
  @Expose() meterReadingEnd?: number;
  @Expose() meterReadingUom?: string;

  // Odometer Fields
  @Expose() odometer?: number
  @Expose() odometerUom?: string

  // Next Stop Fields
  @Expose() nextStopSiteId?: string;
  @Expose() nextStopNote?: string;
  @Expose() nextStopDeclaredDttm?: number;

  // Seal Fields
  @Expose() sealOff?: string;
  @Expose() sealOn?: string;
  @Expose() seal2Off?: string;
  @Expose() seal2On?: string;
  @Expose() seal3Off?: string;
  @Expose() seal3On?: string;
  @Expose() sealOffDttm?: number;
  @Expose() sealOnDttm?: number;
  @Expose() seal2OffDttm?: number;
  @Expose() seal2OnDttm?: number;
  @Expose() seal3OffDttm?: number;
  @Expose() seal3OnDttm?: number;

  @Transform(getDatabaseCodeTransformer(toValveType))
  @Expose()
  sealValveType?: ValveType

  @Transform(getDatabaseCodeTransformer(toValveType))
  @Expose()
  seal2ValveType?: ValveType

  @Transform(getDatabaseCodeTransformer(toValveType))
  @Expose()
  seal3ValveType?: ValveType

  // Oil Specific Fields
  @Expose() standardQuantity?: number
  @Expose() netQuantity?: number;
  @Expose() observedTemperature?: number;
  @Expose() observedPressure?: number
  @Expose() pressureUnitOfMeasure?: string
  @Expose() observedGravity?: number;
  @Expose() bswPercentage?: number;
  @Expose({name: "gravityAt60"}) correctedGravity?: number;
  @Expose() topTemperature?: number;
  @Expose() bottomTemperature?: number;
  @Expose() temperatureUom?: string;
  @Expose()
  @Transform(getDatabaseCodeTransformer(toTransportRejectionType))
  rejectionType?: TransportRejectionType;
  @Expose() rejectionComment?: string;

  // rrc fields
  @Expose()
  @Type(() => RrcEp10WasteManifestBuildStatusEntity)
  rrcEp10WasteManifestBuildStatus?: RrcEp10WasteManifestBuildStatusEntity

  // Other
  @Expose()
  @Type(() => GeofenceEventEntity)
  geofenceEvent?: GeofenceEventEntity;

  @Expose()
  @Type(() => MessageEntity)
  messages?: MessageEntity[]

  @Expose()
  @Exclude({toClassOnly: true})
  supplementals?: Supplemental[]

  @Expose()
  @Type(() => RestrictionEntity)
  restrictions?: RestrictionEntity[]

  @Expose()
  wellStageIsDefault?: boolean

  @Expose()
  readonly projectedWellStageIsDefault?: boolean

  @Expose()
  subStage?: string

  @Expose()
  readonly companyId?: string

  @Expose()
  readonly companyType?: string

  @Expose()
  readonly siteParentSiteName?: string

  @Expose()
  readonly containerClusterName?: string

  @Expose()
  readonly containerScadaMode?: string

  @Expose()
  readonly containerLactMeterAttached?: boolean

  @Expose()
  readonly scadaQty2?: number

  @Expose()
  readonly triggeredAlerts?: t.TypeOf<typeof SystemAlertBaseValidator>[]

  @Expose()
  readonly holdingRecord?: boolean

  @Expose()
  readonly tabletHolding?: boolean

  readonly entityType = 'TRANSPORT';

  get displayName(): Readonly<string> {
    return `Transport ID: ${this.id}`;
  }

  /**
   * Constructor for TransportEntity. Recommend using the {@link create} method instead.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  loadTypeValue(): Promise<Transport> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(TransportValidator, object);
  }

  asTypeValue(): Transport {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSONNow(TransportValidator, object);
  }

  /**
   * Create a TransportEntity from a Transport type value.
   *
   * @param transport The transport type value.
   */
  static from(transport: Transport): TransportEntity {
    return plainToClass(TransportEntity, transport, {
      excludeExtraneousValues: true,
    });
  }

  get coordinates(): GeoCoord | undefined {
    /* istanbul ignore next */
    if (this.latCoord && this.longCoord) {
      return {
        latCoord: this.latCoord,
        longCoord: this.longCoord,
      };
    } else {
      return undefined;
    }
  }

  set coordinates(coordinates: GeoCoord | undefined) {
    /* istanbul ignore next */
    this.latCoord = coordinates ? coordinates.latCoord : undefined;
    /* istanbul ignore next */
    this.longCoord = coordinates ? coordinates.longCoord : undefined;
  }
}

export type TransportPayload = Partial<Omit<Transport, 'messages' | 'gauges'>> & {
  messages?: Partial<Message>[]
  gauges?: Partial<TransportGauge>[]
}
